import { featureCardDecorationEllipse } from "assets/landing/features";
import clsx from "clsx";
import React, { PropsWithChildren, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import crossIcon from "assets/landing/cross-icon.svg";

const COLORS = ["#FFA686", "#CFB2FF", "#FFDF86"];

type FeatureCardProps = {
  title: string;
  cardClasses?: string;
  analytics?: String[];
  isSecondRow?: boolean;
};

const FeatureCard: React.FC<PropsWithChildren<FeatureCardProps>> = ({
  children,
  title,
  cardClasses = "",
  analytics,
  isSecondRow = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const matches = useMediaQuery("(min-width: 1024px)");

  const handleOpen = () => {
    if (matches) {
      return;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <article
      onClick={(e) => {
        e.stopPropagation();
        handleOpen();
      }}
      className={clsx(
        "bg-[#F3F3F8] cursor-pointer lg:w-full max-lg:max-w-full group relative overflow-hidden max-lg:h-[96.8vw] linear-border custom-shadow h-[25.92vw] pt-6 px-6",
        cardClasses
      )}
    >
      <div className="absolute inset-0 z-[12] backdrop-blur-[15px] max-lg:hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      <img
        className="absolute inset-0 z-[10] w-full h-full object-cover max-lg:hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        src={featureCardDecorationEllipse}
        alt="card decoration ellipse"
      />

      {/* MOBILE */}
      <>
        <>
          <div
            className={clsx(
              "absolute inset-0 z-[12] backdrop-blur-[15px] lg:hidden opacity-0 transition-opacity duration-400 pointer-events-none",
              isOpen && "opacity-100"
            )}
          />
          <img
            className={clsx(
              "absolute inset-0 z-[10] w-full h-full object-cover lg:hidden opacity-0 transition-opacity duration-400 pointer-events-none",
              isOpen && "opacity-100"
            )}
            src={featureCardDecorationEllipse}
            alt="card decoration ellipse"
          />
        </>
      </>

      <h3 className="text-36 leading-[1.1] max-lg:text-[8.536vw] font-luxora-regular z-20 relative text-black mb-6">
        {title}
      </h3>
      {!matches && isOpen && (
        <ul
          className={clsx(
            "opacity-0 lg:hidden transition-opacity duration-700 ease-in-out overflow-hidden z-20 relative flex flex-col gap-4 delay-[400ms]",
            isOpen && "opacity-100"
          )}
        >
          {analytics.map((desc, index) => (
            <li
              key={index}
              className="flex items-center max-lg:items-start gap-3"
            >
              <span
                className={clsx(
                  "size-[0.857vw] max-lg:size-[3.2vw] flex-shrink-0 max-lg:mt-[0.5vw] rounded-full",

                  isSecondRow && "items-start mt-[1.2vw]"
                )}
                style={{ background: `${COLORS[index]}` }}
              />
              <p className="text-18 max-lg:text-[4.28vw] leading-[1.1] font-normal text-black">
                {desc}
              </p>
            </li>
          ))}
        </ul>
      )}
      {matches && analytics && (
        <ul
          className={clsx(
            "opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-[300px] transition-all duration-500 ease-in-out overflow-hidden z-20 relative flex flex-col gap-4",
            isSecondRow && "lg:max-w-[28.5vw]"
          )}
        >
          {analytics.map((desc, index) => (
            <li
              key={index}
              className="flex items-center max-lg:items-start gap-3"
            >
              <span
                className={clsx(
                  "size-3 max-lg:size-[3.2vw] flex-shrink-0 max-lg:mt-[0.5vw]  rounded-full",
                  isSecondRow && "lg:items-start lg:-mt-[1.2vw]"
                )}
                style={{ background: `${COLORS[index]}` }}
              />
              <p className="text-18 whitespace-pre-line max-lg:text-[4.28vw] leading-[1.1] font-normal text-black">
                {desc}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div>{children}</div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        className={clsx(
          "lg:hidden size-[8.5vw] absolute rounded-[3vw] right-[6.5vw] custom-shadow bottom-[6.5vw] z-30 flex items-center justify-center bg-white opacity-0 transition-opacity duration-500",
          isOpen && "opacity-100 pointer-events-auto"
        )}
      >
        <img src={crossIcon} alt="cross icon" className="size-[3vw]" />
      </button>
    </article>
  );
};

export default FeatureCard;
